import {
  Box,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../../components/ui/Button";
import Text from "../../../../components/ui/Text";
import { useAuth } from "../../../../contexts/AuthContext";
import { useToast } from "../../../../contexts/ToastContext";
import apiService from "../../../../services/api";
import { formatGameDateTime } from "../../../../utils/formatters";
import { formatTeamSize } from "../../../../utils/game";
import { PayDirectly, Players } from "../../ReviewBooking/parts";
import { AddPlayerDialog } from "./AddPlayerDialog";

export const JoinGameDrawer = ({
  open,
  onClose,
  gameDetails,
  isInvite = false,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [bookingPlayers, setBookingPlayers] = useState(
    currentUser ? [currentUser] : [],
  );
  const [isAddPlayerModalOpen, setIsAddPlayerModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const unregistered_players = bookingPlayers
    .filter((p) => !p.id)
    .map((player) => ({
      first_name: player.first_name,
      last_name: player.last_name,
    }));

  const handleAcceptInvite = async () => {
    try {
      await apiService.respondToGameInvite(gameDetails.id, {
        action: "accept",
      });
    } catch (error) {
      showToast(error.error, "error");
    }
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const response = await apiService.joinGame(gameDetails.id, {
        unregistered_players,
      });
      if (response.success) {
        if (isInvite) {
          await handleAcceptInvite();
        }
        showToast(response.message, "success");
        navigate("/game-success", {
          state: {
            gameDetails,
            playersAdded: unregistered_players.length > 0,
          },
        });
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor={isDesktop ? "right" : "bottom"}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: isDesktop
            ? {
                width: 420,
                maxWidth: "100vw",
                display: "flex",
                flexDirection: "column",
              }
            : {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
              },
        },
      }}
    >
      {!isDesktop && (
        <Box
          sx={{
            width: 40,
            height: 4,
            backgroundColor: "#E5E5E5",
            borderRadius: 2,
            mx: "auto",
            mt: 1.5,
            mb: 1,
            flexShrink: 0,
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: isDesktop ? 3 : 1,
          pb: 1,
          flexShrink: 0,
        }}
      >
        <Text variant="pageHeader" sx={{ fontSize: "20px", mb: 0 }}>
          Join game
        </Text>
        <IconButton onClick={onClose} size="small">
          <IconX size={20} />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, pb: 2, flexShrink: 0 }}>
        <Text component="p" variant="paragraph">
          {formatTeamSize(gameDetails.players)} {gameDetails.sport} at{" "}
          {gameDetails?.venue || "venue"}{" "}
          {formatGameDateTime(gameDetails.datetime)}
        </Text>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3 }}>
        <Players
          bookingPlayers={bookingPlayers}
          gameData={gameDetails}
          openAddPlayerModal={() => setIsAddPlayerModalOpen(true)}
          handleRemovePlayer={(playerId) =>
            setBookingPlayers((prevPlayers) =>
              prevPlayers.filter((player) => player.player_id !== playerId),
            )
          }
        />
        <PayDirectly />
      </Box>

      <Box sx={{ px: 3, py: 2, borderTop: "1px solid #E5E5E5", flexShrink: 0 }}>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={isSubmitting}
          sx={{ width: "100%", py: 1.5, fontSize: "16px" }}
        >
          {isSubmitting ? "Confirming..." : "Confirm booking"}
        </Button>
      </Box>

      <AddPlayerDialog
        isOpen={isAddPlayerModalOpen}
        onClose={() => setIsAddPlayerModalOpen(false)}
        onSubmit={(newPlayerData) => {
          setIsAddPlayerModalOpen(false);
          setBookingPlayers((prevPlayers) => [
            ...prevPlayers,
            { ...newPlayerData, player_id: crypto.randomUUID() },
          ]);
        }}
        players={bookingPlayers}
      />
    </Drawer>
  );
};
